/**
 * Did the PREVIOUS booth UI die, and how? Reported by the next one.
 *
 * A renderer that crashes cannot say so. Main reloads it 1.5 s later
 * (recoverRenderer), the in-memory session is gone, and until now the only trace
 * was a `render-process-gone` line in main's log file on R2. That is how Bara's
 * booth crashed 17 times in 15 days (7 out of memory), two of them taking paid
 * sessions with them, while the dashboard showed a healthy booth.
 *
 * How it works:
 * - Every renderer keeps a small record in localStorage: when it booted, when it
 *   was last alive (every 30 s and on every session change), which session was in
 *   progress and whether it was paid, which page, how much memory.
 * - Leaving properly marks the record clean: `pagehide` (a reload, a navigation,
 *   a window closed the normal way) or main's `app:will-close` request (the
 *   booth's own close path holds the window and then DESTROYS it, which skips
 *   `pagehide`, verified on Electron 24.8.8).
 * - The next renderer reads the record. Not clean means the last one died, and:
 *     same main process (main says so)   -> crash, or hang if main's watchdog
 *                                           killed it, with main's reason (oom ...)
 *     a new main process                 -> unclean_exit: power cut, killed from
 *                                           Task Manager, forced Windows restart
 *   Shells older than main's lifecycle handler fall back to sessionStorage, which
 *   survives a crash reload of the same window but not an app restart (verified
 *   on Electron 24.8.8 too).
 * - It is reported once as a BoothEvent `renderer_restart`, which the Studio
 *   shows on the device timeline, counts toward health, and pages on when it
 *   loops or takes a paid session.
 *
 * Electron only. A browser booth's tab is discarded and restored by the browser
 * at will, which would read as a crash every time.
 */

import type { RendererLifecycleInfo } from '../../../../shared/rendererLifecycle'

export const LIFECYCLE_RECORD_KEY = 'dreambooth.rendererLifecycle.v1'
export const LIFECYCLE_WINDOW_KEY = 'dreambooth.rendererWindow.v1'
/** How often the record's `lastAliveAt` moves: the precision of "when it died". */
export const ALIVE_INTERVAL_MS = 30_000
/** An unclean end older than this is history, not something to report on boot. */
export const MAX_REPORT_AGE_MS = 30 * 24 * 60 * 60 * 1000
/**
 * A clean mark that is still alive this long afterwards was not an exit after
 * all (a close that never completed), so it stops counting. Main gives the close
 * a few seconds at most.
 */
export const CLEAN_MARK_EXPIRY_MS = 15_000

export interface LifecycleRecord {
  v: 1
  bootedAt: number
  lastAliveAt: number
  cleanExit: boolean
  cleanAt?: number | null
  cleanReason?: string | null
  /** main's start time when this renderer booted; null on shells that do not report it. */
  mainStartedAt?: number | null
  appVersion?: string | null
  deviceId?: string | null
  projectId?: string | null
  /** The session in progress, if any. */
  sessionId?: string | null
  /** That session was settled (paid) and not completed. */
  paid?: boolean
  route?: string | null
  heapUsedMB?: number | null
}

export type RestartKind = 'crash' | 'hang' | 'unclean_exit'

export interface RestartReport {
  kind: RestartKind
  /** 'main' when main's lifecycle info decided it, 'heuristic' when storage did. */
  detection: 'main' | 'heuristic'
  reason: string | null
  exitCode: number | null
  /** When the dead renderer was last known alive, epoch ms. */
  at: number
  /** From then to this boot: how long the booth was down. */
  downMs: number
  /** How long the dead renderer had been running. */
  uptimeMs: number
  sessionId: string | null
  paid: boolean
  route: string | null
  heapUsedMB: number | null
  appVersion: string | null
  previousAppVersion: string | null
  /** When the dead renderer booted: with the device, it names this death uniquely. */
  previousBootedAt: number
  projectId: string | null
  deviceId: string | null
}

export interface ClassifyInput {
  previous: LifecycleRecord | null
  /** The window marker from sessionStorage was present at boot. */
  sameWindow: boolean
  now: number
  appVersion: string | null
  main: RendererLifecycleInfo | null
}

/**
 * How far around the dead run's last sign of life a crash dump still counts as
 * its death. It died after `lastAliveAt` and before its next touch, one
 * ALIVE_INTERVAL_MS later; the slack covers a timer running late on a busy booth.
 */
export const CRASH_DUMP_MATCH_BEFORE_MS = 2_000
export const CRASH_DUMP_MATCH_AFTER_MS = ALIVE_INTERVAL_MS + 15_000

/** A crash dump was written when the previous run died (shells from B16 on). */
export function diedWithCrashDump(lastAliveAt: number, dumpTimes: number[] | undefined): boolean {
  if (!Array.isArray(dumpTimes)) return false
  return dumpTimes.some(
    (at) =>
      typeof at === 'number' &&
      at >= lastAliveAt - CRASH_DUMP_MATCH_BEFORE_MS &&
      at <= lastAliveAt + CRASH_DUMP_MATCH_AFTER_MS
  )
}

/**
 * The verdict on the previous renderer, or null when there is nothing to report
 * (first boot, a clean exit, an update restart, or ancient history).
 */
export function classifyPreviousRun(input: ClassifyInput): RestartReport | null {
  const prev = input.previous
  if (
    !prev ||
    prev.v !== 1 ||
    typeof prev.bootedAt !== 'number' ||
    typeof prev.lastAliveAt !== 'number'
  ) {
    return null
  }
  if (prev.cleanExit) return null
  if (input.now - prev.lastAliveAt > MAX_REPORT_AGE_MS) return null

  let kind: RestartKind
  let detection: RestartReport['detection'] = 'heuristic'
  let reason: string | null = null
  let exitCode: number | null = null

  if (input.main && typeof prev.mainStartedAt === 'number') {
    if (input.main.mainStartedAt === prev.mainStartedAt) {
      // Same app run: main reloaded the window after the renderer died. Its exit
      // is the newest one main recorded after that renderer booted.
      const exit = [...input.main.exits].reverse().find((e) => e.at >= prev.bootedAt)
      if (exit) {
        kind = exit.viaHangWatchdog ? 'hang' : 'crash'
        detection = 'main'
        reason = exit.reason
        exitCode = exit.exitCode
      } else {
        kind = 'crash'
      }
    } else {
      kind = 'unclean_exit'
      detection = 'main'
      // The app died at the moment a crash dump was written: it crashed (main,
      // GPU, a native module), it did not lose power or get ended by hand.
      if (diedWithCrashDump(prev.lastAliveAt, input.main.crashDumpTimes)) reason = 'crash_dump'
    }
  } else {
    kind = input.sameWindow ? 'crash' : 'unclean_exit'
  }

  // An installer that stops the app to replace it is not an incident.
  if (
    kind === 'unclean_exit' &&
    input.appVersion &&
    prev.appVersion &&
    input.appVersion !== prev.appVersion
  ) {
    return null
  }

  const sessionId = typeof prev.sessionId === 'string' && prev.sessionId ? prev.sessionId : null
  return {
    kind,
    detection,
    reason,
    exitCode,
    at: prev.lastAliveAt,
    downMs: Math.max(0, input.now - prev.lastAliveAt),
    uptimeMs: Math.max(0, prev.lastAliveAt - prev.bootedAt),
    sessionId,
    paid: sessionId != null && prev.paid === true,
    route: prev.route ?? null,
    heapUsedMB: typeof prev.heapUsedMB === 'number' ? prev.heapUsedMB : null,
    appVersion: input.appVersion,
    previousAppVersion: prev.appVersion ?? null,
    previousBootedAt: prev.bootedAt,
    projectId: prev.projectId ?? null,
    deviceId: prev.deviceId ?? null
  }
}

// ---------------------------------------------------------------------------
// The live part: keeps this renderer's record, reports the previous one.
// ---------------------------------------------------------------------------

export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/** What the booth is doing right now, for the record. */
export interface LifecycleSnapshot {
  deviceId?: string | null
  projectId?: string | null
  sessionId?: string | null
  paid?: boolean
  route?: string | null
  heapUsedMB?: number | null
}

export interface LifecycleDeps {
  local: KeyValueStore | null
  session: KeyValueStore | null
  now: () => number
  getMainInfo: () => Promise<RendererLifecycleInfo | null>
  getAppVersion: () => Promise<string | null>
  snapshot: () => LifecycleSnapshot
  report: (report: RestartReport) => void
  /** Subscribe to "the session changed"; returns an unsubscribe. */
  onSessionChange: (listener: () => void) => () => void
  setInterval: (fn: () => void, ms: number) => unknown
  addPageHideListener: (fn: () => void) => void
}

let current: LifecycleRecord | null = null
let activeDeps: LifecycleDeps | null = null

function readRecord(store: KeyValueStore | null): LifecycleRecord | null {
  if (!store) return null
  try {
    const raw = store.getItem(LIFECYCLE_RECORD_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LifecycleRecord
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

function writeRecord(): void {
  if (!current || !activeDeps?.local) return
  try {
    activeDeps.local.setItem(LIFECYCLE_RECORD_KEY, JSON.stringify(current))
  } catch {
    // Storage full or blocked: monitoring must never break the booth.
  }
}

/** Refresh `lastAliveAt` and what the booth is doing. */
export function touchRendererLifecycle(): void {
  if (!current || !activeDeps) return
  const now = activeDeps.now()
  if (
    current.cleanExit &&
    current.cleanAt != null &&
    now - current.cleanAt > CLEAN_MARK_EXPIRY_MS
  ) {
    current.cleanExit = false
    current.cleanAt = null
    current.cleanReason = null
  }
  current.lastAliveAt = now
  try {
    Object.assign(current, activeDeps.snapshot())
  } catch {
    // A store not ready yet: keep the last snapshot.
  }
  writeRecord()
}

/**
 * This renderer is leaving on purpose. Called from `pagehide` and from main's
 * `app:will-close` request (see utils/cameras/gracefulQuit).
 */
export function markRendererCleanExit(reason: string): void {
  if (!current || !activeDeps) return
  current.cleanExit = true
  current.cleanAt = activeDeps.now()
  current.cleanReason = reason
  current.lastAliveAt = current.cleanAt
  writeRecord()
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ms)
      })
    ])
  } catch {
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Start tracking this renderer and report the previous one. Idempotent.
 *
 * This boot's record is written BEFORE anything is awaited: if this renderer
 * dies while main is still answering, the next one must find it unclean.
 */
export async function startRendererLifecycle(deps: LifecycleDeps): Promise<RestartReport | null> {
  if (activeDeps) return null
  activeDeps = deps

  const previous = readRecord(deps.local)
  let sameWindow = false
  try {
    sameWindow = deps.session?.getItem(LIFECYCLE_WINDOW_KEY) != null
  } catch {
    sameWindow = false
  }

  const bootedAt = deps.now()
  current = { v: 1, bootedAt, lastAliveAt: bootedAt, cleanExit: false, ...safeSnapshot(deps) }
  writeRecord()
  try {
    deps.session?.setItem(LIFECYCLE_WINDOW_KEY, String(bootedAt))
  } catch {
    // ignore
  }

  deps.addPageHideListener(() => markRendererCleanExit('unload'))
  deps.setInterval(touchRendererLifecycle, ALIVE_INTERVAL_MS)
  deps.onSessionChange(touchRendererLifecycle)

  const [main, appVersion] = await Promise.all([
    withTimeout(deps.getMainInfo(), 3000),
    withTimeout(deps.getAppVersion(), 3000)
  ])
  if (current) {
    current.mainStartedAt = main?.mainStartedAt ?? null
    current.appVersion = appVersion ?? null
    writeRecord()
  }

  const report = classifyPreviousRun({
    previous,
    sameWindow,
    now: bootedAt,
    appVersion: appVersion ?? null,
    main: main ?? null
  })
  if (report) {
    try {
      deps.report(report)
    } catch (err) {
      console.warn('[LIFECYCLE] Could not record the previous renderer exit:', err)
    }
  }
  return report
}

function safeSnapshot(deps: LifecycleDeps): LifecycleSnapshot {
  try {
    return deps.snapshot()
  } catch {
    return {}
  }
}

/** Test seam: forget the running tracker. */
export function resetRendererLifecycleForTests(): void {
  current = null
  activeDeps = null
}

/** The V8 heap in use, MB, where the browser exposes it (Chromium/Electron do). */
/**
 * The BoothEvent id for one renderer death: the same death always gets the same
 * id, so a report filed twice is stored once (the batch route upserts on it).
 *
 * It does get filed twice when this renderer cannot write its own record
 * (storage full or blocked): the dead one's record is then never replaced, every
 * later boot finds it again, and with a random id each report counted as a new
 * crash, enough to turn health Warning and page a "crash loop" from one death.
 * FNV-1a keeps the id short: legacy deviceIds are ~290 characters.
 */
export function restartEventId(deviceId: string, previousBootedAt: number): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < deviceId.length; i++) {
    hash ^= deviceId.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return `rr-${previousBootedAt}-${hash.toString(16).padStart(8, '0')}`
}

export function readJsHeap(): { usedMB: number; limitMB: number } | null {
  const memory = (
    performance as unknown as { memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number } }
  ).memory
  if (!memory || typeof memory.usedJSHeapSize !== 'number') return null
  return {
    usedMB: Math.round((memory.usedJSHeapSize / (1024 * 1024)) * 10) / 10,
    limitMB:
      typeof memory.jsHeapSizeLimit === 'number'
        ? Math.round(memory.jsHeapSizeLimit / (1024 * 1024))
        : 0
  }
}

/** The current page, path only (HashRouter on desktop), bounded. */
export function currentRoute(): string | null {
  if (typeof window === 'undefined') return null
  const raw = window.location.hash
    ? window.location.hash.replace(/^#/, '')
    : window.location.pathname
  const path = raw.split('?')[0]
  return path ? path.slice(0, 120) : null
}
